/**
 * Support 模块服务端 API（FAQ / 用户手册 / 反馈）
 *
 * 与 ADM 桌面端同一套接口（src/service/apis）。
 * 鉴权头与 adm requestConfig 一致：x-token = sha256(timestamp + SECRET)。
 */
import axios, { type AxiosError } from 'axios'
import { reportHttpError } from '@/monitoring/sentry'

const http = axios.create()
http.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    reportHttpError(error, {
      status: error.response?.status,
      method: error.config?.method,
      url: error.config?.url,
    })
    return Promise.reject(error)
  },
)

const BASE_SECRET = 'u*88ZP8Tvpu_-EuEZ'
const PC_ID_KEY = 'adm.web.pcid'

/**
 * API 基地址：
 * - dev：空串（同源），由 vite proxy /app_api 转发，本地无跨域问题；
 * - 生产：直连 API 域 —— web 静态站的网关（APISIX）没有 /app_api 转发路由，
 *   同源 POST 会 405；API 域已对 web 域名开 CORS（allow-origin 精确放行），直连即可。
 *   同一份构建会部署到 CI / BETA / MP 多个域名，API 主机按当前访问域名自动匹配
 *   （与 adm 桌面端 appInfo.baseUrl() 的环境映射一致），否则 beta 站点会跨域打到
 *   CI API 被 CORS 拦截（FAQ / 手册 / 反馈全挂）。
 *   可用构建期环境变量 VITE_API_BASE 覆盖（如指向生产 API）。
 *   注意：部分云端打包机会注入空字符串，空值要当作未配置处理。
 */
function resolveApiBase(): string {
  const explicit = import.meta.env.VITE_API_BASE as string | undefined
  if (explicit) return explicit
  if (import.meta.env.DEV) return ''
  const host = (typeof location !== 'undefined' && location.hostname) || ''
  if (host.includes('-beta')) return 'https://ankerdock-api-beta.anker.com'
  if (host === 'ankerdockmanager-pre.anker.com') return 'https://ankerdock-api.anker.com'
  if (host === 'ankerdockmanager.anker.com') return 'https://ankerdock-api.anker.com'
  // CI 及未知域名保持原有行为，走 CI API
  return 'https://ankerdock-api-ci.anker.com'
}

const API_BASE: string = resolveApiBase()

async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** web 无 machineId，用持久化随机 id 代替 */
function getPcId(): string {
  try {
    let id = localStorage.getItem(PC_ID_KEY)
    if (!id) {
      id = crypto.randomUUID?.() || String(Date.now()) + Math.random().toString(16).slice(2)
      localStorage.setItem(PC_ID_KEY, id)
    }
    return id
  } catch {
    return 'web'
  }
}

async function buildHeaders(locale: string): Promise<Record<string, string>> {
  const timestamp = Math.floor(Date.now() / 1000)
  const type = /mac/i.test(navigator.platform || '') ? 'mac' : 'win'
  return {
    'x-token': await sha256Hex(timestamp + BASE_SECRET),
    'x-timestamp': String(timestamp),
    'x-language': locale,
    'x-user-agent': `AnkerDockManager-web-1.0.0`,
    'x-software-version': '1.0.0',
    'x-software-channel': '1',
    'x-os-type': type,
    'x-os-version': '',
    'x-pc-id': getPcId(),
  }
}

/** app_type 枚举：1=DNA 产品（与 adm 桌面端 APP_TYPE 一致） */
const APP_TYPE = 1

async function post<T = any>(url: string, data: any, locale: string): Promise<T> {
  const headers = await buildHeaders(locale)
  // 服务端从 query 读 app_type（CI/BETA 已切换口径，body 里的会被忽略），统一补上
  const res = await http.post(API_BASE + url, data, {
    headers,
    timeout: 30_000,
    params: { app_type: APP_TYPE },
  })
  return res.data
}

async function get<T = any>(url: string, params: Record<string, any>, locale: string): Promise<T> {
  const headers = await buildHeaders(locale)
  const res = await http.get(API_BASE + url, {
    headers,
    timeout: 30_000,
    params: { app_type: APP_TYPE, ...params },
  })
  return res.data
}

/** 皮肤条目（GET /app_api/skins），字段与云端「皮肤管理」一一对应 */
export interface SkinItem {
  id: number
  product_code: string
  /** 四语名称，key 与前端 locale 一致：zh-CN / en-US / ja-JP / de-DE */
  name: Record<string, string>
  /** 方形头像 */
  avatar: string
  /** 设备图片 = 舞台底图（人物背景原画） */
  device_img: string
  /** 预览图组 = 机壳屏显区轮播的图（≤5 张） */
  preview_shots: string[]
  sort_weight: number
  /** 该皮肤 flash 素材版本，如 "V2.0.8"，与固件文件头 "verX.Y.Z" 对应 */
  flash_version: string
  /** 皮肤固件下载地址（Web 端只预览，用不到；App 端换肤下载用） */
  fw_url: string
  fw_size: number
  /** 注意：后端此字段当前存的是 MD5（32 位 hex），并非真 SHA256 */
  fw_sha256: string
}

/**
 * 皮肤清单（GET /app_api/skins，query 带 product_code）。
 * 只会下发后台「上架」状态的皮肤；顺序以服务端为准（按权重降序）。
 */
export async function fetchSkinList(productCode: string, locale: string): Promise<SkinItem[]> {
  const body = await get('/app_api/skins', { product_code: productCode }, locale)
  const list = body?.data
  return Array.isArray(list) ? list : []
}

export interface FaqItem {
  question: string
  answer: string
  pn?: string
}

/** FAQ 列表（POST /app_api/faqs，body {pn_list}） */
export async function fetchFaqList(pn: string, locale: string): Promise<FaqItem[]> {
  const body = await post('/app_api/faqs', { pn_list: [pn] }, locale)
  const list = body?.data
  if (Array.isArray(list) && list.length > 0 && Array.isArray(list[0]?.faqs)) {
    return list[0].faqs
  }
  return []
}

/**
 * 用户手册 PDF 地址（POST /app_api/products/{pn}）。
 * 返回体 data.data 是 { 'en-US': url, 'en-US-md5': md5, ... } 的多语言映射；
 * 当前 locale 缺失时回退 en-US，再回退任意 .pdf 链接。
 */
export async function fetchUserManualUrl(pn: string, locale: string): Promise<string> {
  const body = await post(`/app_api/products/${pn}`, { pn, app_type: APP_TYPE }, locale)
  const map = body?.data?.data
  if (!map || typeof map !== 'object') throw new Error('manual not found')
  const candidate = map[locale] || map['en-US']
  if (typeof candidate === 'string' && candidate.toLowerCase().endsWith('.pdf')) return candidate
  for (const v of Object.values(map)) {
    if (typeof v === 'string' && v.toLowerCase().endsWith('.pdf')) return v
  }
  throw new Error('manual not found')
}

/** 手册 S3 桶；dev 仍走 Vite proxy，生产直接访问 S3（云端已放行 CORS） */
const MANUAL_CDN_HOST = 'ankerdock-ci.s3.us-west-2.amazonaws.com'

/**
 * 把 S3 手册 URL 转为可下载地址。
 * - dev：走 /manual-cdn，由 Vite proxy 转发，避免 localhost CORS 限制；
 * - prod：直接访问 S3，避免依赖 web 静态站网关配置 /manual-cdn 反代。
 * S3 对象 Content-Type 仍可能是 image/jepg，所以下载后统一重标 application/pdf。
 * 未知域名原样返回（兜底直接打开）。
 */
export function toSameOriginManualUrl(url: string): string {
  try {
    const u = new URL(url)
    if (u.hostname === MANUAL_CDN_HOST && import.meta.env.DEV) return '/manual-cdn' + u.pathname
  } catch {
    /* 非法 URL 原样返回 */
  }
  return url
}

/** 校验 PDF 魔数 %PDF：避免网关 fallback 或错误资源被当 PDF 嵌入 */
function assertPdfBytes(head: Uint8Array | undefined): void {
  if (
    !head ||
    head.length < 4 ||
    head[0] !== 0x25 || // %
    head[1] !== 0x50 || // P
    head[2] !== 0x44 || // D
    head[3] !== 0x46 // F
  ) {
    throw new Error('manual proxy missing or response is not a PDF')
  }
}

/**
 * 经同源代理下载手册并重新标注为 application/pdf 的 Blob URL（供 iframe 内嵌渲染）。
 * onProgress 回调 0-100（响应无 Content-Length 时不回调）。
 */
export async function fetchManualBlobUrl(
  url: string,
  onProgress?: (percent: number) => void,
): Promise<string> {
  const res = await fetch(toSameOriginManualUrl(url))
  if (!res.ok) {
    const error = new Error(`manual download failed: HTTP ${res.status}`)
    reportHttpError(error, { status: res.status, method: 'get', url })
    throw error
  }
  const total = Number(res.headers.get('content-length')) || 0
  if (!res.body || !total) {
    const buf = new Uint8Array(await res.arrayBuffer())
    assertPdfBytes(buf.subarray(0, 4))
    return URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }))
  }
  const reader = res.body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    if (value) {
      if (received === 0) assertPdfBytes(value.subarray(0, 4))
      chunks.push(value)
      received += value.length
      onProgress?.(Math.min(100, Math.round((received / total) * 100)))
    }
  }
  assertPdfBytes(chunks[0]?.subarray(0, 4))
  return URL.createObjectURL(new Blob(chunks as BlobPart[], { type: 'application/pdf' }))
}

export interface FeedbackPayload {
  message: string
  email: string
  pn: string
  sn?: string
}

/** 提交反馈（POST /app_api/feedbacks）。返回 'ok' | 'email' | 'fail' */
export async function submitFeedback(
  payload: FeedbackPayload,
  locale: string,
): Promise<'ok' | 'email' | 'fail'> {
  const body = await post(
    '/app_api/feedbacks',
    {
      message: payload.message,
      email: payload.email,
      pn: payload.pn,
      sn: payload.sn || '',
      log_file_url: '',
      source_from: 'web',
      type: 1,
    },
    locale,
  )
  if (body?.code === 2000) return 'ok'
  if (body?.msg === 'Email must be a valid email address') return 'email'
  return 'fail'
}
